import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/shared/lib/api'
import type {
  MarketListItem,
  MarketOverview,
  KeywordItem,
  BudgetReco,
  SimulateRequest,
  SimulateResponse,
  OpportunitiesResponse,
  SetupStatus,
  JobRunItem,
  ApiEnvelope,
  ApiPagination,
} from './sdi.types'

// ── Markets ──────────────────────────────────────────────────

export const useMarkets = (params?: { status?: string; page?: number; per_page?: number }) =>
  useQuery({
    queryKey: ['sdi', 'markets', params],
    queryFn: () =>
      api.get<ApiEnvelope<MarketListItem[]>>('/sdi/markets', { params }).then((r) => r.data),
  })

export const useMarket = (id: string) =>
  useQuery({
    queryKey: ['sdi', 'markets', id],
    queryFn: () =>
      api.get<ApiEnvelope<MarketOverview>>(`/sdi/markets/${id}`).then((r) => r.data.data),
    enabled: !!id,
  })

export const useCreateMarket = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; country: string; language: string; currency?: string; seed_keyword?: string; keywords?: string[] }) =>
      api.post('/sdi/markets', data).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sdi'] }),
  })
}

export const useUpdateMarket = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; status?: string; country?: string; language?: string }) =>
      api.patch(`/sdi/markets/${id}`, data).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sdi'] }),
  })
}

// ── Keywords ─────────────────────────────────────────────────

export const useKeywords = (marketId: string, params?: { status?: string; cluster_group?: string }) =>
  useQuery({
    queryKey: ['sdi', 'keywords', marketId, params],
    queryFn: () =>
      api.get<ApiEnvelope<KeywordItem[]>>(`/sdi/markets/${marketId}/keywords`, { params }).then((r) => r.data.data),
    enabled: !!marketId,
  })

export const useReplaceKeywords = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ marketId, keywords }: { marketId: string; keywords: { keyword: string; status?: string; tag?: string; cluster_group?: string }[] }) =>
      api.post(`/sdi/markets/${marketId}/keywords`, { keywords }).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sdi'] }),
  })
}

// ── Sync & Backfill ──────────────────────────────────────────

export const useTriggerSync = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (marketId: string) =>
      api.post(`/sdi/markets/${marketId}/sync`).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sdi'] }),
  })
}

export const useTriggerBackfill = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ marketId, months }: { marketId: string; months?: number }) =>
      api.post(`/sdi/markets/${marketId}/backfill`, { months: months ?? 24 }).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sdi'] }),
  })
}

// ── Budget & Simulation ──────────────────────────────────────

export const useBudgetReco = (marketId: string, targetCapture?: number) =>
  useQuery({
    queryKey: ['sdi', 'budget', marketId, targetCapture],
    queryFn: () =>
      api.get<ApiEnvelope<BudgetReco>>(`/sdi/markets/${marketId}/budget`, {
        params: targetCapture ? { target_capture: targetCapture } : undefined,
      }).then((r) => r.data.data),
    enabled: !!marketId,
  })

export const useSimulateBudget = () =>
  useMutation({
    mutationFn: ({ marketId, ...data }: { marketId: string } & SimulateRequest) =>
      api.post<ApiEnvelope<SimulateResponse>>(`/sdi/markets/${marketId}/simulate`, data).then((r) => r.data.data),
  })

// ── Opportunities ────────────────────────────────────────────

export const useOpportunities = (marketId: string, limit?: number) =>
  useQuery({
    queryKey: ['sdi', 'opportunities', marketId, limit],
    queryFn: () =>
      api.get<ApiEnvelope<OpportunitiesResponse>>(`/sdi/markets/${marketId}/opportunities`, {
        params: limit ? { limit } : undefined,
      }).then((r) => r.data.data),
    enabled: !!marketId,
  })

// ── Jobs ─────────────────────────────────────────────────────

export const useJobHistory = (marketId: string, params?: { job_name?: string; limit?: number }) =>
  useQuery({
    queryKey: ['sdi', 'jobs', marketId, params],
    queryFn: () =>
      api.get<ApiEnvelope<JobRunItem[]>>(`/sdi/markets/${marketId}/jobs`, { params }).then((r) => r.data.data),
    enabled: !!marketId,
  })

// ── Setup ────────────────────────────────────────────────────

export const useSetupStatus = (marketId: string) =>
  useQuery({
    queryKey: ['sdi', 'setup', marketId],
    queryFn: () =>
      api.get<ApiEnvelope<SetupStatus>>(`/sdi/markets/${marketId}/setup`).then((r) => r.data.data),
    enabled: !!marketId,
  })

// ── Connectors ───────────────────────────────────────────────

export const useReconnectConnector = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ marketId, source }: { marketId: string; source: string }) =>
      api.post(`/sdi/markets/${marketId}/connectors/${source}/reconnect`).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sdi'] }),
  })
}

// ── CSV Import ───────────────────────────────────────────────

export const useImportCsv = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ marketId, file }: { marketId: string; file: File }) => {
      const formData = new FormData()
      formData.append('file', file)
      return api.post(`/sdi/markets/${marketId}/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then((r) => r.data.data)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sdi'] }),
  })
}
